'use client'
import { useInlineMemoizedContentGetter, useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { TitleDivider } from 'components/TitleDivider'
import { CardCarousel } from 'components/Carousels/CardCarousel'
import { ThreeProductCard } from 'components/ThreeProductCard'
import { AchieveTheme } from '@achieve/ascend'
import { LinkButton, Typography } from 'components/Contentful'
import classNames from 'classnames'
import { FdrPage } from 'utils/environmentConfig'
import {} from 'react'

const CARD_CAROUSEL_MINIMUM_LENGTH = 4

function ProductCards({ content }) {
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  const { eyebrow, title, subtitle, grid, config, cardLink, disclosure, linkButton } =
    useMemoizedContentGetter(content, [
      'eyebrow',
      'title',
      'subtitle',
      'grid',
      'config',
      'cardLink',
      'disclosure',
      'linkButton',
    ])
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'

  const { jsonContent } = useInlineMemoizedContentGetter(content?.fields?.additionalConfiguration, [
    'jsonContent',
  ])

  const titleVariant = config?.jsonContent?.titleVariant || 'displayLg'
  const titleMobile = config?.jsonContent?.titleVariantMobile || 'displaySm'

  // Cards that re in draft mode won't have the `fields` prop
  const publishedCards = (grid?.gridItemsCollection?.items || []).filter((item) => Boolean(item))

  if (!publishedCards.length) return null

  const carouselEnabled =
    jsonContent?.mobileHorizontalScroll && isMobile
      ? true
      : publishedCards.length >= CARD_CAROUSEL_MINIMUM_LENGTH
  const CardLayoutComponent = carouselEnabled ? CardCarousel : ThreeProductCard

  const backgroundColor = jsonContent?.backgroundColor
  const eyebrowColor = jsonContent?.eyebrowColor
  const classNameSubtitleText = config?.jsonContent?.classNameSubtitleText
  const typographicOptions = config?.jsonContent?.typographicOptions || {}
  const backgroundColorMap = {
    'light-blue': AchieveTheme?.ascend?.colors?.blue?.[50],
    blue: AchieveTheme?.ascend?.colors?.blue?.[25],
    white: AchieveTheme?.ascend?.colors?.neutral?.[0],
    grey: AchieveTheme?.ascend?.colors?.neutral[25],
  }

  return (
    <Section
      backgroundColor={
        backgroundColorMap[backgroundColor] ?? AchieveTheme?.ascend?.colors?.neutral?.[0]
      }
      className={'py-8 px-0 lg:py-16 lg:px-0'}
      childContainerClassName={
        carouselEnabled &&
        'group-data-narrow:max-w-full group-data-narrow:p-0 lg:p-0 flex flex-col gap-8'
      }
      data-testid="product-card-section"
    >
      <TitleDivider
        eyebrow={eyebrow}
        eyebrowBlack={eyebrowColor ?? eyebrow?.fontColor === 'Black'}
        eyebrowClassName={`${
          eyebrowColor ? eyebrowColor : eyebrow?.fontColor
        } font-bold tracking-eyebrow`}
        title={title}
        subtitle={subtitle}
        dataTestId="product-card-section-title-area"
        classNameTitleText={`${titleMobile} lg:${titleVariant} font-${
          config?.jsonContent?.titleFontWeight || 'medium'
        }`}
        classNameSubtitleText={
          classNameSubtitleText !== undefined
            ? classNameSubtitleText
            : 'bodySm lg:displaySm font-light'
        }
        className={'mb-4'}
      />
      {linkButton && (
        <div className="flex justify-center my-6">
          {' '}
          <LinkButton
            className={classNames('min-w-[293px] h-[47px] font-bold', {
              'bg-[#cb000e]': siteEnv === FdrPage,
            })}
            typographicOptions={{
              className: 'bodySm lg:bodyLg font-bold! text-white',
            }}
            content={linkButton}
          />
        </div>
      )}
      {grid && (
        <CardLayoutComponent
          content={{
            gridItems: publishedCards,
            className: `${jsonContent?.variant === 'CardIconList' ? 'pt-0' : ''}`,
          }}
          isMobile={isMobile}
          productCardVariant={jsonContent?.variant || 'ProductCard'}
        />
      )}

      {cardLink && (
        <div className="flex justify-center">
          <LinkButton
            className={classNames('min-w-[293px] h-[47px] font-semibold', {
              'bg-[#cb000e]': siteEnv === FdrPage,
            })}
            typographicOptions={typographicOptions}
            content={cardLink}
          />
        </div>
      )}
      {disclosure && (
        <div className="mt-4">
          <Typography
            content={disclosure?.textContent}
            variant="bodyXs"
            className={classNames(
              'max-w-[808px] text-center m-auto',
              'text-[#42546b] leading-tight pt-2'
            )}
            data-testid={'product-card-disclosure'}
          />
        </div>
      )}
    </Section>
  )
}
export { ProductCards }
export default ProductCards
